import { useState, useEffect, useMemo } from 'react'
import { useRecoilState } from 'recoil'
import { isFromSearchState, alarmReviewSelectedTimeState, selectBedInfoState } from '../plugins/recoil'
import moment from 'moment'

import {ReactComponent as IconLeftArrowTripple} from '../assets/icons/ic-arrow-left-tripple.svg'
import {ReactComponent as IconRightArrowTripple} from '../assets/icons/ic-arrow-right-tripple.svg'

const Timeline = ({ selectedAlarmInfo, patientHistories, alarmList, startTime, endTime, handlePrevTripple, handleNextTripple }) => {
    const [selectedTime] = useRecoilState(alarmReviewSelectedTimeState)
    const [isFromSearch] = useRecoilState(isFromSearchState)
    const [selectBedInfo] = useRecoilState(selectBedInfoState)

    const eventColor = (eventPrioirity) => {
        switch (eventPrioirity) {
            case 0:
                return '#FFF500'; // Yellow
            case 1:
                return '#FFF500'; // Yellow
            case 2:
                return '#FF002E'; // Red
            default:
                return '#FFFFFF'; // Default White
        }
    }

    const generateTimeSlots = (start, end) => {
        const timeSlots = []
        const startMoment = moment(start)
        const endMoment = moment(end)
        
        let current = startMoment.clone()
        
        while (current.isBefore(endMoment)) {
            timeSlots.push(current.clone())
            current.add(1, 'hour') // 1시간 단위로 변경
        }
        
        return timeSlots
    }

    const getGlobalSections = () => {
        const timelineStart = moment(startTime)
        const timelineEnd = moment(endTime)
        
        // 전체 구분선을 시간순으로 정렬
        const allDividers = timelinePatientRenderInfos
            .filter(info => info.type === 'divider')
            .sort((a, b) => moment(a.eventTime).diff(moment(b.eventTime)))
        
        const globalSections = []
        let sectionStart = timelineStart.clone()
        
        // 첫 번째 구분선 이전 구간 (타임라인 시작 ~ 첫 구분선)
        if (allDividers.length > 0) {
            const firstDividerTime = moment(allDividers[0].eventTime)
            globalSections.push({
                startTime: sectionStart.clone(),
                endTime: firstDividerTime.clone(),
                sectionIndex: 0
            })
            sectionStart = firstDividerTime.clone()
        }
        
        // 구분선 사이의 구간들
        allDividers.forEach((divider, index) => {
            if (index < allDividers.length - 1) {
                const nextDividerTime = moment(allDividers[index + 1].eventTime)
                globalSections.push({
                    startTime: sectionStart.clone(),
                    endTime: nextDividerTime.clone(),
                    sectionIndex: index + 1
                })
                sectionStart = nextDividerTime.clone()
            }
        })
        
        // 마지막 구분선 이후 구간 (마지막 구분선 ~ 타임라인 끝)
        if (allDividers.length > 0) {
            globalSections.push({
                startTime: sectionStart.clone(),
                endTime: timelineEnd.clone(),
                sectionIndex: allDividers.length
            })
        } else {
            // 구분선이 없는 경우 전체가 하나의 구간
            globalSections.push({
                startTime: timelineStart.clone(),
                endTime: timelineEnd.clone(),
                sectionIndex: 0
            })
        }
        
        return globalSections
    }

    const getPatientHistoryRenderInfo = () => {
        const timelineStart = moment(startTime)
        const timelineEnd = moment(endTime)
        const totalDurationSeconds = timelineEnd.diff(timelineStart, 'seconds')
        
        const allEvents = patientHistories
            .map(history => ({
                ...history,
                timestamp: moment(history.createdAt)
            }))
            .sort((a, b) => a.timestamp.diff(b.timestamp))
        
        const renderInfos = []
        
        allEvents.forEach((event, index) => {
            const eventTime = event.timestamp
            
            if (eventTime.isBetween(timelineStart, timelineEnd, null, '[]')) {
                const offsetSeconds = eventTime.diff(timelineStart, 'seconds')
                const leftPercent = (offsetSeconds / totalDurationSeconds) * 100
                
                renderInfos.push({
                    type: 'divider',
                    leftPercent,
                    eventTime: event.createdAt,
                    action: event.action,
                    patientName: event.patientName,
                    bedNumber: event.devicePatientId
                })
            }
        })
        
        const patientGroups = {}
        patientHistories.forEach(history => {
            if (!patientGroups[history.patientId]) {
                patientGroups[history.patientId] = []
            }
            patientGroups[history.patientId].push(history)
        })
        
        Object.keys(patientGroups).forEach(patientId => {
            const histories = patientGroups[patientId].sort((a, b) => moment(a.createdAt).diff(moment(b.createdAt)))
            
            let currentAdmitted = null
            
            histories.forEach((history, index) => {
                if (history.action === 'ADMITTED') {
                    currentAdmitted = history
                } else if (history.action === 'UNASSIGNED' && currentAdmitted) {
                    const eventStart = moment(currentAdmitted.createdAt)
                    const eventEnd = moment(history.createdAt)
                    
                    if (eventStart.isBefore(timelineEnd) && eventEnd.isAfter(timelineStart)) {
                        const renderStart = moment.max(eventStart, timelineStart)
                        const renderEnd = moment.min(eventEnd, timelineEnd)
                        
                        const offsetSeconds = renderStart.diff(timelineStart, 'seconds')
                        const durationSeconds = renderEnd.diff(renderStart, 'seconds')
                        
                        const leftPercent = (offsetSeconds / totalDurationSeconds) * 100
                        const widthPercent = (durationSeconds / totalDurationSeconds) * 100
                        
                        renderInfos.push({
                            type: 'patient',
                            ...currentAdmitted,
                            leftPercent,
                            widthPercent,
                            isAssigned: false,
                            patientName: currentAdmitted.patientName || 'Unknown',
                            bedNumber: currentAdmitted.devicePatientId || 'N/A',
                            admittedTime: currentAdmitted.createdAt,
                            unassignedTime: history.createdAt
                        })
                    }
                    currentAdmitted = null
                }
            })
            
            if (currentAdmitted) {
                const eventStart = moment(currentAdmitted.createdAt)
                const eventEnd = timelineEnd
                
                if (eventStart.isBefore(timelineEnd) && eventEnd.isAfter(timelineStart)) {
                    const renderStart = moment.max(eventStart, timelineStart)
                    const renderEnd = moment.min(eventEnd, timelineEnd)
                    
                    const offsetSeconds = renderStart.diff(timelineStart, 'seconds')
                    const durationSeconds = renderEnd.diff(renderStart, 'seconds')
                    
                    const leftPercent = (offsetSeconds / totalDurationSeconds) * 100
                    const widthPercent = (durationSeconds / totalDurationSeconds) * 100
                    
                    renderInfos.push({
                        type: 'patient',
                        ...currentAdmitted,
                        leftPercent,
                        widthPercent,
                        isAssigned: true,
                        patientName: currentAdmitted.patientName || 'Unknown',
                        bedNumber: currentAdmitted.devicePatientId || 'N/A',
                        admittedTime: currentAdmitted.createdAt,
                        unassignedTime: null
                    })
                }
            }
        })
        
        return renderInfos
    }

    const getTimelineEventRenderInfo = () => {
        const timelineStart = moment(startTime)
        const timelineEnd = moment(endTime)
        const totalDurationSeconds = timelineEnd.diff(timelineStart, 'seconds')
        
        return alarmList.map(event => {
            const eventStart = moment(event.startTime)
            const eventEnd = moment(event.endTime)
            
            if (eventStart.isBefore(timelineEnd) && eventEnd.isAfter(timelineStart)) {
                const renderStart = moment.max(eventStart, timelineStart)
                const renderEnd = moment.min(eventEnd, timelineEnd)
                
                const offsetSeconds = renderStart.diff(timelineStart, 'seconds')
                const durationSeconds = renderEnd.diff(renderStart, 'seconds')
                
                const leftPercent = (offsetSeconds / totalDurationSeconds) * 100
                const widthPercent = (durationSeconds / totalDurationSeconds) * 100
                
                return {
                    ...event,
                    leftPercent,
                    widthPercent,
                    color: eventColor(event.eventPriority)
                }
            }
            return null
        }).filter(Boolean)
    }

    const calculateTextLeftPosition = (text, isFirstSlot = false) => {
        const charWidth = 5.8; 
        const textWidth = text.length * charWidth;
        
        if (isFirstSlot) {
            return 2; 
        }
        
        return -(textWidth / 2);
    };

    const timeSlots = useMemo(() => {
        return generateTimeSlots(startTime, endTime)
    }, [startTime, endTime])

    const slotWidth = useMemo(() => {
        return timeSlots.length > 0 ? `calc(100% / ${timeSlots.length})` : '100%'
    }, [timeSlots.length])
    
    const timelineEventRenderInfos = useMemo(() => {
        return getTimelineEventRenderInfo()
    }, [alarmList, startTime, endTime])
    
    const timelinePatientRenderInfos = useMemo(() => {
        return getPatientHistoryRenderInfo()
    }, [patientHistories, startTime, endTime])
    
    const globalSections = useMemo(() => {
        return getGlobalSections()
    }, [timelinePatientRenderInfos, startTime, endTime])
    
    const prevDisabled = isFromSearch ? (moment(startTime).isSameOrBefore(selectBedInfo.startTime)) : false
    const nextDisabled = isFromSearch ? (moment(endTime).isSameOrAfter(moment(selectBedInfo.endTime))) : (moment(endTime).add(1, 'days').isAfter(moment()))

    useEffect(() => {
        // console.log('Updated Timeline:', patientHistories);
    }, [patientHistories]);
    
    return (
        <div className="flex h-full px-5 pt-3 items-start relative">
            {selectedAlarmInfo && selectedAlarmInfo.startTime && (
                (() => {
                    const selectedMoment = moment(selectedAlarmInfo.startTime)
                    const timelineStart = moment(startTime)
                    const timelineEnd = moment(endTime)
                    
                    // selectedTime이 타임라인 범위 내에 있는지 확인
                    if (selectedMoment.isBetween(timelineStart, timelineEnd, null, '[]')) {
                        const totalDurationSeconds = timelineEnd.diff(timelineStart, 'seconds')
                        const offsetSeconds = selectedMoment.diff(timelineStart, 'seconds')
                        const leftPercent = (offsetSeconds / totalDurationSeconds) * 100
                        
                        // 버튼 영역 제외하고 타임라인 영역에서의 위치 계산
                        const leftButtonWidth = 65 // 42px + 3px margin + 20px padding
                        const rightButtonWidth = 65 // 42px + 3px margin
                        
                        return (
                            <div 
                                className="absolute top-0 w-[1px] border-dashed z-40"
                                style={{ 
                                    left: `calc(${leftButtonWidth}px + (100% - ${leftButtonWidth + rightButtonWidth}px) * ${leftPercent / 100})`,
                                    height: '100%',
                                    borderRight: '1px solid #CECECE'
                                }}
                            />
                        )
                    }
                    return null
                })()
            )}
            <div 
                className="cursor-pointer flex items-center justify-center h-[26px] bg-[#2C2C2C] mr-[3px]"
                style={{ borderTopLeftRadius: 888, borderBottomLeftRadius: 888, minWidth: 42, maxWidth: 42, opacity: prevDisabled ? 0.3 : 1, cursor: prevDisabled ? 'default' : 'pointer' }}
                onClick={() => { if (!prevDisabled) handlePrevTripple() }}>
                <IconLeftArrowTripple />
            </div>
            <div className="flex-1 h-[26px]">
                <div className="flex w-full">
                    {timeSlots.map((timeMoment, index) => {
                        const currentDate = timeMoment.format('YYYY-MM-DD')
                        const prevDate = index > 0 ? timeSlots[index - 1].format('YYYY-MM-DD') : currentDate
                        const isDateChanged = currentDate !== prevDate
                    
                        const slotStart = timeMoment
                        const slotEnd = timeMoment.clone().add(1, 'hour')

                        const overlappingSections = globalSections.filter(section => 
                            section.startTime.isBefore(slotEnd) && section.endTime.isAfter(slotStart)
                        )

                        // selectedAlarmInfo의 시간 범위
                        const selectedAlarmStart = selectedAlarmInfo ? moment(selectedAlarmInfo.startTime) : null
                        const selectedAlarmEnd = selectedAlarmInfo ? moment(selectedAlarmInfo.endTime) : null
                        
                        // 빗금 구간 생성
                        const hatchSections = []
                        
                        if (!isFromSearch) {
                            overlappingSections.forEach((section, sectionIndex) => {
                                // 슬롯과 섹션의 겹치는 부분 계산
                                const overlapStart = moment.max(section.startTime, slotStart)
                                const overlapEnd = moment.min(section.endTime, slotEnd)
                                
                                const offsetSeconds = overlapStart.diff(slotStart, 'seconds')
                                const durationSeconds = overlapEnd.diff(overlapStart, 'seconds')
                                
                                const leftPercent = (offsetSeconds / 3600) * 100
                                const widthPercent = (durationSeconds / 3600) * 100
                                
                                // selectedAlarmInfo와 겹치는지 확인
                                const isOverlapWithSelectedAlarm = selectedAlarmStart && selectedAlarmEnd &&
                                    selectedAlarmStart.isBefore(section.endTime) && selectedAlarmEnd.isAfter(section.startTime)
                                
                                hatchSections.push({
                                    left: leftPercent,
                                    width: widthPercent,
                                    shouldShowHatch: !isOverlapWithSelectedAlarm
                                })
                            })
                        }

                        const slotEvents = timelineEventRenderInfos.filter(eventInfo => {
                            const eventStart = moment(eventInfo.startTime)
                            const eventEnd = moment(eventInfo.endTime)
                            return eventStart.isBefore(slotEnd) && eventEnd.isAfter(slotStart)
                        })

                        // 구분선 정보 필터링
                        const slotDividers = timelinePatientRenderInfos.filter(dividerInfo =>
                            dividerInfo.type === 'divider' &&
                            moment(dividerInfo.eventTime).isBetween(slotStart, slotEnd, null, '[]')
                        )

                        return (
                            <div 
                                key={index}
                                className="relative h-[12px] bg-[#2F2F2F] flex items-center"
                                style={{ width: slotWidth }}
                            >
                                {hatchSections.map((section, sectionIndex) => (
                                    <div
                                        key={`hatch-${sectionIndex}`}
                                        className="absolute"
                                        style={{
                                            left: `${section.left}%`,
                                            width: `${section.width}%`,
                                            height: '24px',
                                            top: '0px',
                                            zIndex: 10,
                                            background: section.shouldShowHatch 
                                                ? 'repeating-linear-gradient(135deg, transparent, transparent 2px, #6C6C6C80 2px, #6C6C6C80 4px)'
                                                : 'transparent'
                                        }}
                                    />
                                ))}

                                {slotDividers.map((dividerInfo, dividerIndex) => {
                                    const dividerTime = moment(dividerInfo.eventTime)
                                    const offsetSeconds = dividerTime.diff(slotStart, 'seconds')
                                    const leftPercent = (offsetSeconds / 3600) * 100

                                    return (
                                        <div
                                            key={`divider-${dividerIndex}`}
                                            className="absolute"
                                            style={{
                                                left: `${leftPercent}%`,
                                                width: '0px',
                                                height: '24px', // 시간 표시 바로 위까지
                                                top: '0px',
                                                zIndex: 11
                                            }}
                                            title={`${dividerInfo.action}: ${dividerInfo.patientName || 'Unknown'}`}
                                        >
                                            {/* 위쪽 직각삼각형 */}
                                            <div
                                                className="absolute"
                                                style={{
                                                    top: '0px',
                                                    left: '-3px',
                                                    width: '0',
                                                    height: '0',
                                                    transform: 'rotate(-90deg)',
                                                    borderBottom: '6px solid #CECECE',
                                                    borderLeft: '6px solid transparent'
                                                }}
                                            />
                                            
                                            {/* 세로선 */}
                                            <div
                                                className="absolute"
                                                style={{
                                                    top: '0px',
                                                    left: '3px',
                                                    width: '1px',
                                                    height: '100%', // 위아래 삼각형 제외
                                                    backgroundColor: '#CECECE'
                                                }}
                                            />
                                            
                                            {/* 아래쪽 직각삼각형 */}
                                            <div
                                                className="absolute"
                                                style={{
                                                    bottom: '0px',
                                                    left: '-3px',
                                                    width: '0',
                                                    height: '0',
                                                    transform: 'rotate(90deg)',
                                                    borderTop: '6px solid #CECECE',
                                                    borderLeft: '6px solid transparent'
                                                }}
                                            />
                                        </div>
                                    )
                                })}

                                {slotEvents
                                    .sort((a, b) => a.eventPriority - b.eventPriority)
                                    .map((eventInfo, eventIndex) => {
                                        const eventStart = moment(eventInfo.startTime)
                                        const eventEnd = moment(eventInfo.endTime)
                                        
                                        // 슬롯 내에서의 상대적 위치 계산
                                        const renderStart = moment.max(eventStart, slotStart)
                                        const renderEnd = moment.min(eventEnd, slotEnd)

                                        const offsetSeconds = renderStart.diff(slotStart, 'seconds')
                                        const durationSeconds = renderEnd.diff(renderStart, 'seconds')

                                        const leftPercent = (offsetSeconds / 3600) * 100
                                        const widthPercent = (durationSeconds / 3600) * 100

                                        return (
                                            <div
                                                key={eventIndex}
                                                className="absolute h-full"
                                                style={{
                                                    left: `${leftPercent}%`,
                                                    width: `${widthPercent}%`,
                                                    minWidth: 1,
                                                    backgroundColor: eventInfo.color,
                                                    zIndex: 9
                                                }}
                                            />
                                        )
                                })}

                                <div className="absolute w-[0px] h-[12px] top-[12px]" style={{ border: '1px solid #2F2F2F' }} />
                                {
                                    index === timeSlots.length - 1 &&
                                    <div className="absolute w-[0px] h-[12px] top-[12px] right-0" style={{ border: '1px solid #2F2F2F' }} />
                                }
                                <div className="absolute text-[#DEDEDE] font-light top-[24px]" style={{ fontSize: 12, left: calculateTextLeftPosition(timeMoment.format('HH:mm'), index === 0) }}>
                                    {timeMoment.format('HH:mm')}
                                </div>
                                {isDateChanged && (
                                    <div className="absolute text-[#DEDEDE] font-light top-[40px]" style={{ fontSize: 12, left: calculateTextLeftPosition(`[${timeMoment.format('YY/MM/DD')}]`, index === 0) }}>
                                        {`[${timeMoment.format('YY/MM/DD')}]`}
                                    </div>
                                )}
                                {
                                    index === timeSlots.length - 1 &&
                                    <div className="absolute top-[24px] right-0 text-[#DEDEDE] font-light" style={{ fontSize: 12 }}>
                                        { moment(endTime).format('HH:mm') }
                                    </div>
                                }
                                {index === timeSlots.length - 1 && moment(endTime).format('YYYY-MM-DD') !== currentDate && (
                                    <div className="absolute top-[40px] right-0 text-[#DEDEDE] font-light" style={{ fontSize: 12 }}>
                                        {`[${moment(endTime).format('YY/MM/DD')}]`}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
            <div 
                className="cursor-pointer flex items-center justify-center h-[26px] bg-[#2C2C2C] ml-[3px]"
                style={{ borderTopRightRadius: 888, borderBottomRightRadius: 888, minWidth: 42, maxWidth: 42, opacity: nextDisabled ? 0.3 : 1, cursor: nextDisabled ? 'default' : 'pointer' }}
                onClick={() => { if (!nextDisabled) handleNextTripple() }}>
                <IconRightArrowTripple />
            </div>

        </div>
    )
}

export default Timeline